// OAuth Configuration - Single source of truth for all OAuth settings
export interface OAuthProvider {
  name: string;
  displayName: string;
  supabaseProvider: string; // What Supabase expects
  scopes: string[];
  requiredScopes: string[]; // Scopes needed for core functionality
}

export interface OAuthConfig {
  providers: {
    google: OAuthProvider;
    microsoft: OAuthProvider;
  };
}

export const OAUTH_CONFIG: OAuthConfig = {
  providers: {
    google: {
      name: "google",
      displayName: "Google",
      supabaseProvider: "google",
      scopes: [
        // Core identity scopes
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/userinfo.profile",
        // Contacts access
        "https://www.googleapis.com/auth/contacts.readonly",
        // Email sending capabilities
        "https://www.googleapis.com/auth/gmail.send",
        "https://www.googleapis.com/auth/gmail.compose",
      ],
      requiredScopes: [
        "https://www.googleapis.com/auth/contacts.readonly",
        "https://www.googleapis.com/auth/gmail.send",
        "https://www.googleapis.com/auth/gmail.compose",
      ],
    },
    microsoft: {
      name: "microsoft",
      displayName: "Microsoft",
      supabaseProvider: "azure", // Supabase uses 'azure' for Microsoft
      scopes: [
        // Core identity scopes
        "openid",
        "profile",
        "email",
        // Contacts and email access
        "https://graph.microsoft.com/Contacts.ReadWrite",
        "https://graph.microsoft.com/Mail.ReadWrite",
        "https://graph.microsoft.com/Mail.Send",
      ],
      requiredScopes: [
        "https://graph.microsoft.com/Contacts.ReadWrite",
        "https://graph.microsoft.com/Mail.ReadWrite",
        "https://graph.microsoft.com/Mail.Send",
      ],
    },
  },
};

export type OAuthProviderName = keyof typeof OAUTH_CONFIG.providers;

/**
 * Get OAuth provider configuration
 */
export function getOAuthProvider(provider: OAuthProviderName): OAuthProvider {
  return OAUTH_CONFIG.providers[provider];
}

/**
 * Get scopes for OAuth request
 */
export function getOAuthScopes(provider: OAuthProviderName): string[] {
  return OAUTH_CONFIG.providers[provider].scopes;
}

/**
 * Get required scopes for validation
 */
export function getRequiredScopes(provider: OAuthProviderName): string[] {
  return OAUTH_CONFIG.providers[provider].requiredScopes;
}

/**
 * Check if user scopes include all required scopes
 */
export function hasRequiredScopes(
  userScopes: string[],
  provider: OAuthProviderName
): boolean {
  const required = getRequiredScopes(provider);
  return required.every((scope) => userScopes.includes(scope));
}

/**
 * Get missing scopes for a provider
 */
export function getMissingScopes(
  userScopes: string[],
  provider: OAuthProviderName
): string[] {
  const required = getRequiredScopes(provider);
  return required.filter((scope) => !userScopes.includes(scope));
}
